// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useBlocker } from '@tanstack/react-router';
import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Reusable hook for tracking unsaved form changes.
 *
 * Compares a snapshot of the initial data against the current form data.
 * Provides:
 * - `hasChanges` — reactive dirty flag
 * - `guardClose(action)` — wraps cancel/close with a confirm dialog
 * - `dialogProps` — spread onto `<UnsavedChangesGuard />`
 * - Automatic navigation blocking via TanStack Router's `useBlocker`
 *
 * @param currentData  The current form data object.
 * @param opts.enabled Whether dirty-tracking is active (default: true).
 *                     Pass `false` when the form is not open / not in edit mode.
 *
 * @example
 * ```tsx
 * const unsaved = useUnsavedChanges(formData, { enabled: isFormOpen });
 *
 * function openEdit(data: FormData) {
 *   setFormData(data);
 *   unsaved.snapshot(data);
 * }
 *
 * function handleClose() {
 *   unsaved.guardClose(() => resetForm());
 * }
 *
 * return (
 *   <>
 *     <button disabled={!unsaved.hasChanges}>Save</button>
 *     <button onClick={handleClose}>Cancel</button>
 *     <UnsavedChangesGuard {...unsaved.dialogProps} />
 *   </>
 * );
 * ```
 */
export function useUnsavedChanges<T extends Record<string, unknown>>(
  currentData: T,
  opts: { enabled?: boolean } = {},
) {
  const { enabled = true } = opts;

  const originalData = useRef<T | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);

  /** Store a snapshot of the initial data (call when entering edit mode). */
  const snapshot = useCallback((data: T) => {
    originalData.current = data;
  }, []);

  /** Clear the snapshot (call after a successful save or form reset). */
  const clear = useCallback(() => {
    originalData.current = null;
    setShowDialog(false);
    pendingAction.current = null;
  }, []);

  /** Whether the current data differs from the snapshot. */
  const hasChanges = useMemo(() => {
    if (!enabled || !originalData.current) return false;
    const orig = originalData.current;
    return Object.keys(orig).some(
      (key) => currentData[key] !== orig[key as keyof T],
    );
  }, [enabled, currentData]);

  // Block route navigation when there are unsaved changes.
  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => hasChanges,
    withResolver: true,
    enableBeforeUnload: true,
    disabled: !hasChanges,
  });

  // When the router blocker fires, open our dialog.
  const isBlockedByRouter = status === 'blocked';

  /**
   * Guard a close/cancel action. If there are unsaved changes, opens the
   * confirm dialog instead of executing the action immediately. If there
   * are no changes, executes the action directly.
   */
  const guardClose = useCallback(
    (action: () => void) => {
      if (hasChanges) {
        pendingAction.current = action;
        setShowDialog(true);
      } else {
        action();
      }
    },
    [hasChanges],
  );

  /** Props to spread onto `<UnsavedChangesGuard />`. */
  const dialogProps = useMemo(
    () => ({
      open: showDialog || isBlockedByRouter,
      onOpenChange: (open: boolean) => {
        if (!open) {
          // Dialog closed (Escape, backdrop click, cancel button).
          setShowDialog(false);
          pendingAction.current = null;
          if (isBlockedByRouter) reset();
        }
      },
      onConfirm: () => {
        // Run the pending in-page action if any.
        pendingAction.current?.();
        pendingAction.current = null;
        setShowDialog(false);
        // If the router triggered the block, let navigation proceed.
        if (isBlockedByRouter) proceed();
      },
    }),
    [showDialog, isBlockedByRouter, proceed, reset],
  );

  return {
    /** Whether the form data has diverged from the snapshot. */
    hasChanges,
    /** Store a snapshot of the initial form data. */
    snapshot,
    /** Clear the snapshot (after save or reset). */
    clear,
    /** Guard a close/cancel action with a confirmation dialog. */
    guardClose,
    /** Props for `<UnsavedChangesGuard />`. */
    dialogProps,
  };
}
